import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { on } from '@ember/modifier';
import { Text, Heading } from 'tio-ui/components/typography';
import { eq } from 'tio-ui/utilities';
import {
  PhCheckCircle,
  PhHourglass,
  PhClock,
  PhListBullets,
  PhFlowArrow,
  PhBank,
} from 'ember-phosphor-icons';
import Timeline from 'tio-reimagined/components/timeline';
import Chip from 'tio-reimagined/components/chip';
import { formatCurrency, formatCycleDate, formatNumericDate } from 'tio-reimagined/format';
import type { ContributionPaymentRecord } from 'tio-employee/services/reimagined-data';

/**
 * ContributionTimeline — Displays contribution payment history as a visual timeline or table
 *
 * Provides two views:
 * - Timeline: Visual vertical timeline with status nodes and payment details
 * - Table: Traditional table with benefit cycle, paid date, servicer, amount columns
 *
 * @example
 * ```gts
 * <ContributionTimeline @payments={{this.payments}} />
 * ```
 */
interface ContributionTimelineSignature {
  Args: {
    /** Array of contribution payment records to display */
    payments: ContributionPaymentRecord[];
  };
  Element: HTMLDivElement;
}

export default class ContributionTimeline extends Component<ContributionTimelineSignature> {
  @tracked viewMode: 'timeline' | 'table' = 'timeline';

  get pastPayments() {
    return this.args.payments.filter((p) => !p.isFuture);
  }

  switchToTimeline = () => {
    this.viewMode = 'timeline';
  };

  switchToTable = () => {
    this.viewMode = 'table';
  };

  <template>
    {{! template-lint-disable no-bare-strings }}
    <div class="bg-white rounded-xl shadow-sm ring-1 ring-gray-950/[0.04]" ...attributes>
      {{! Header with view toggle }}
      <div class="flex items-center justify-between p-5 pb-0">
        <div class="flex items-center gap-2">
          <PhBank @size={{22}} class="text-gray-600" />
          <Heading @level={{3}} class="text-lg font-semibold text-gray-900">Payment History</Heading>
          {{#if this.pastPayments.length}}
            <Chip @intent="default" @size="sm">{{this.pastPayments.length}} payments</Chip>
          {{/if}}
        </div>

        <div class="flex items-center bg-gray-100 rounded-lg p-0.5">
          <button
            type="button"
            class="flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium transition-colors
              {{if
                (eq this.viewMode 'timeline')
                'bg-white shadow-sm text-gray-900'
                'text-gray-500 hover:text-gray-700'
              }}"
            {{on "click" this.switchToTimeline}}
          >
            <PhFlowArrow @size={{16}} />
            Timeline
          </button>
          <button
            type="button"
            class="flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium transition-colors
              {{if
                (eq this.viewMode 'table')
                'bg-white shadow-sm text-gray-900'
                'text-gray-500 hover:text-gray-700'
              }}"
            {{on "click" this.switchToTable}}
          >
            <PhListBullets @size={{16}} />
            Table
          </button>
        </div>
      </div>

      <div class="p-5">
        {{#if this.pastPayments.length}}
          {{#if (eq this.viewMode "timeline")}}
            {{! Timeline View }}
            <Timeline
              @items={{this.pastPayments}}
              @lineClass="bg-violet-200"
              @connectorClass="left-[15px]"
            >
              <:node as |payment|>
                <div
                  class="w-8 h-8 rounded-full flex items-center justify-center
                    {{if
                      (eq payment.status 'sent')
                      'bg-emerald-100 text-emerald-600'
                      (if
                        (eq payment.status 'in_progress')
                        'bg-blue-100 text-blue-600'
                        'bg-gray-100 text-gray-400'
                      )
                    }}"
                >
                  {{#if (eq payment.status "sent")}}
                    <PhCheckCircle @size={{18}} />
                  {{else if (eq payment.status "in_progress")}}
                    <PhHourglass @size={{18}} />
                  {{else}}
                    <PhClock @size={{18}} />
                  {{/if}}
                </div>
              </:node>
              <:content as |payment|>
                <div class="flex items-center justify-between py-1">
                  <div>
                    <div class="font-medium text-gray-900">
                      {{formatCycleDate payment.benefitCycle}}
                    </div>
                    <div class="flex items-center gap-2 mt-0.5">
                      {{#if payment.servicer}}
                        <Text @intent="muted" class="text-sm">{{payment.servicer}}</Text>
                        <span class="text-gray-300">·</span>
                      {{/if}}
                      <Text @intent="muted" class="text-sm">
                        {{if payment.paidDate (formatNumericDate payment.paidDate) "Pending"}}
                      </Text>
                    </div>
                  </div>
                  <div class="text-right">
                    <div class="font-semibold text-gray-900">{{formatCurrency payment.amount}}</div>
                    <Chip
                      @intent={{if
                        (eq payment.status "sent")
                        "success"
                        (if (eq payment.status "in_progress") "primary" "default")
                      }}
                      @size="sm"
                      class="mt-0.5"
                    >
                      {{if
                        (eq payment.status "sent")
                        "Sent"
                        (if (eq payment.status "in_progress") "In Progress" "Pending")
                      }}
                    </Chip>
                  </div>
                </div>
              </:content>
            </Timeline>
          {{else}}
            {{! Table View }}
            <div class="overflow-x-auto">
              <table class="w-full">
                <thead>
                  <tr class="border-b border-gray-200">
                    <th class="text-left py-3 px-4 text-sm font-medium text-gray-500">Benefit Cycle</th>
                    <th class="text-left py-3 px-4 text-sm font-medium text-gray-500">Paid Date</th>
                    <th class="text-left py-3 px-4 text-sm font-medium text-gray-500">Servicer</th>
                    <th class="text-left py-3 px-4 text-sm font-medium text-gray-500">Status</th>
                    <th class="text-right py-3 px-4 text-sm font-medium text-gray-500">Amount</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-gray-100">
                  {{#each this.pastPayments as |payment|}}
                    <tr class="hover:bg-gray-50 transition-colors">
                      <td class="py-3 px-4 text-sm text-gray-900">{{formatCycleDate
                          payment.benefitCycle
                        }}</td>
                      <td class="py-3 px-4 text-sm text-gray-600">
                        {{if payment.paidDate (formatNumericDate payment.paidDate) "—"}}
                      </td>
                      <td class="py-3 px-4 text-sm text-gray-600">{{if
                          payment.servicer
                          payment.servicer
                          "—"
                        }}</td>
                      <td class="py-3 px-4">
                        <Chip
                          @intent={{if
                            (eq payment.status "sent")
                            "success"
                            (if (eq payment.status "in_progress") "primary" "default")
                          }}
                          @size="sm"
                        >
                          {{if
                            (eq payment.status "sent")
                            "Sent"
                            (if (eq payment.status "in_progress") "In Progress" "Pending")
                          }}
                        </Chip>
                      </td>
                      <td
                        class="py-3 px-4 text-sm font-medium text-gray-900 text-right"
                      >{{formatCurrency payment.amount}}</td>
                    </tr>
                  {{/each}}
                </tbody>
              </table>
            </div>
          {{/if}}
        {{else}}
          {{! Empty State }}
          <div class="text-center py-8">
            <div
              class="p-4 bg-gray-50 rounded-full w-16 h-16 mx-auto mb-3 flex items-center justify-center"
            >
              <PhBank @size={{32}} class="text-gray-400" />
            </div>
            <Text class="font-medium text-gray-900">No Payments Yet</Text>
            <Text @intent="muted" class="text-sm mt-1">
              Contribution payments will appear here once your employer begins sending them.
            </Text>
          </div>
        {{/if}}
      </div>
    </div>
  </template>
}
